@if (overviewData) {
<div class="generic-overview-wrapper">
  <!-- Header: same pattern as AI Citations (title left, download right) -->
  <div class="overview-tab-info">
    <div class="overview-title-row">
      <h2 class="overview-title">Overview</h2>
      @if (bulkBackgroundProgress; as bg) {
        <div class="overview-silent-bulk-progress" role="status" aria-live="polite">
          <mat-spinner diameter="18" class="silent-bulk-spinner"></mat-spinner>
          <span class="silent-bulk-text">{{ bg.loaded }}/{{ bg.total }} phases completed</span>
          @if (bg.currentTab) {
            <span class="silent-bulk-tab">· {{ bg.currentTab }}</span>
          }
          <button mat-icon-button type="button" class="silent-bulk-stop" (click)="onStopBackgroundBulk()"
                  matTooltip="Stop loading remaining tabs" aria-label="Stop background tab loading">
            <mat-icon>stop</mat-icon>
          </button>
        </div>
      }
    </div>
    <div class="overview-header-actions">
      @if (showOverviewReload) {
        <button mat-stroked-button color="primary" (click)="onReload()" class="overview-reload-button"
                title="Reload overview (dev only)">
          <mat-icon>refresh</mat-icon>
          Reload
        </button>
        <button mat-stroked-button color="primary" (click)="onReloadOtherTabs()" class="overview-reload-button"
        title="Reload remaining tabs in background">
        <mat-icon>autorenew</mat-icon>
        Reload Other Tabs
      </button>
    }
      <button mat-button (click)="onDownloadOverview()" class="export-btn">
        <mat-icon>file_download</mat-icon>
        Download Overview
      </button>
    </div>
  </div>

  <div class="generic-overview-container" #overviewPrintable>
  <!-- Client name + website: visible in PDF only (hidden on screen via CSS) -->
  <div class="overview-pdf-only-header" aria-hidden="true">
    <strong>{{ clientName }}</strong>
    @if (clientWebsite) {
      <span class="pdf-website">{{ clientWebsite }}</span>
    }
  </div>

  <!-- Top Row: Left Column (Score Widget + AI Visibility) and Chart -->
  <div class="top-row">
    <!-- Left Column: AI Visibility (Business Overview Score hidden) -->
    <div class="left-column">
      @if (isPromptsBuildOverview()) {
      <div class="prompts-build-ai-vis-note card-like">
        <h4 class="subsection-title">AI Visibility index</h4>
        <p class="note-text">AI Visibility metrics are still processing or failed. Chart above reflects prompt coverage.</p>
        <div class="prompts-build-gauge">
          <app-semi-circle [score]="{ domain: [0, 100], value: compositeScore }"></app-semi-circle>
        </div>
      </div>
      } @else {
      <div class="ai-visibility-section">
        <app-generic-ai-visibility 
          [llmComparisonData]="llmComparisonData" 
          [llmComparisonMetricsProgress]="llmComparisonMetricsProgress"
          [isLLMComparisonMetricsLoading]="isLLMComparisonMetricsLoading"
          [llmComparisonMetricsTaskId]="llmComparisonMetricsTaskId"
          [showInlineProgress]="false"
          [hideReloadButton]="true"
          [showReloadProgress]="showProgressBelowLoader"
          (filterChange)="onCitedPagesFilterChange($event)"
          (cancelTask)="onCancelLLMComparisonMetrics($event)"
          (reloadMetrics)="onReloadLLMComparisonMetrics()">
        </app-generic-ai-visibility>
      </div>
      }
    </div>

    <!-- Business Overview Chart -->
    <div class="chart-section">
      <div class="chart-header">
        <div class="chart-title-section">
          <h4 class="chart-title">Business Performance Analysis</h4>
          <p class="chart-subtitle">Comprehensive business metrics comparison across brands</p>
        </div>
      </div>
      <!-- Business Overview Chart -->
      <div class="chart-mobile-wrapper">
        @if(results.length > 0 || lineChart.length > 0) {
        <div class="combo-chart-wrapper">
          <app-bar-line-combo-chart [view]="view" [scheme]="comboBarScheme" [colorSchemeLine]="lineChartScheme"
            [results]="results" [lineChart]="lineChart" [barBrandName]="firstBrandName || 'Your Brand'"
            [animations]="true" [tooltipDisabled]="false" [yAxisTickFormatting]="yLeftTickFormat"
            [yLeftAxisScaleFactor]="yLeftAxisScale" [yRightAxisScaleFactor]="yRightAxisScale"
            [yRightAxisTickFormatting]="yRightTickFormat" [gradient]="false" [xAxis]="true" [yAxis]="true"
            [legend]="true" [legendTitle]="'Brands'" [legendPosition]="'right'" [showGridLines]="true"
            [showXAxisLabel]="true" [showYAxisLabel]="true" [showRightYAxisLabel]="true"
            [xAxisLabel]="'Business Metrics'" [yAxisLabel]="'Score'" [yAxisLabelRight]="'Score'" [noBarWhenZero]="true"
            [wrapTicks]="false" (select)="onSelect($event)" (activate)="onActivate($event)"
            (deactivate)="onDeactivate($event)">
          </app-bar-line-combo-chart>
        </div>
        }
        @if(false && tableRows.length > 0) {
          <app-common-performance-table [rows]="tableRows" [columns]="tableColumns" [title]="'Business Performance Overview'">
          </app-common-performance-table>
          }
      </div>
    </div>
  </div>

  @if (tablePromptCitationSummary.length > 0) {
    <div class="prompt-citation-summary-block">
      <h4 class="prompt-citation-summary-title">Prompt &amp; citation summary</h4>
      <p class="prompt-citation-summary-subtitle">Branded and generic prompts/citations as share % of combined total (branded + generic)</p>
      <div class="prompt-citation-summary-table-scroll">
        <table class="prompt-citation-summary-table" role="grid">
          <thead>
            <tr>
              <th scope="col">Brands</th>
              <th scope="col">Branded prompts</th>
              <th scope="col">Generic prompts</th>
              <th scope="col">Branded citation</th>
              <th scope="col">Generic citation</th>
            </tr>
          </thead>
          <tbody>
            @for (row of tablePromptCitationSummary; track row.brand) {
              <tr>
                <th scope="row" class="brand-cell">{{ row.brand }}</th>
                <td>
                  <span class="metric-value">
                    {{ formatPercentCell(row.prompt_citation_summary?.branded_prompts?.value) }}
                  </span>
                  <!-- <span class="metric-delta" [ngClass]="changePercentClass(row.prompt_citation_summary?.branded_prompts?.change_percent)">
                    {{ formatChangePercent(row.prompt_citation_summary?.branded_prompts?.change_percent) }}
                  </span> -->
                </td>
                <td>
                  <span class="metric-value">
                    {{ formatPercentCell(row.prompt_citation_summary?.generic_prompts?.value) }}
                  </span>
                  <!-- <span class="metric-delta" [ngClass]="changePercentClass(row.prompt_citation_summary?.generic_prompts?.change_percent)">
                    {{ formatChangePercent(row.prompt_citation_summary?.generic_prompts?.change_percent) }}
                  </span> -->
                </td>
                <td>
                  <span class="metric-value">
                    {{ formatPercentCell(row.prompt_citation_summary?.branded_citation?.value) }}
                  </span>
                  <!-- <span class="metric-delta" [ngClass]="changePercentClass(row.prompt_citation_summary?.branded_citation?.change_percent)">
                    {{ formatChangePercent(row.prompt_citation_summary?.branded_citation?.change_percent) }}
                  </span> -->
                </td>
                <td>
                  <span class="metric-value">
                    {{ formatPercentCell(row.prompt_citation_summary?.generic_citation?.value) }}
                  </span>
                  <!-- <span class="metric-delta" [ngClass]="changePercentClass(row.prompt_citation_summary?.generic_citation?.change_percent)">
                    {{ formatChangePercent(row.prompt_citation_summary?.generic_citation?.change_percent) }}
                  </span> -->
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
    }

  <!-- Performance Table -->
  <!-- @if(tableRows.length > 0) {
  <app-common-performance-table [rows]="tableRows" [columns]="tableColumns" [title]="'Business Performance Overview'">
  </app-common-performance-table>
  } -->

  <!-- AI Top Prompts (prompt–response cards by platform) -->
  @if (getTopPromptsByPlatform().length > 0) {
  <section class="top-prompts-by-platform-section">
    <h3 class="section-title">AI Top Prompts</h3>
    <p class="section-subtitle">Prompt–answer pairs by platform. Click a card for full response and citations.</p>
    <div class="top-prompts-cards" [class.expanded]="topPromptsExpanded">
      @for (item of getVisibleTopPrompts(); track item.platform + item.query) {
        <button type="button" class="top-prompt-card" (click)="openTopPromptDialog(item)">
          <div class="card-header-row">
            <span class="card-platform-icon" [attr.aria-label]="getPlatformLabel(item.platform)">
              @switch (item.platform) {
                @case ('chatgpt') {
                  <img src="/images/logo/chatgpt.png" alt="ChatGPT" />
                }
                @case ('gemini') {
                  <img src="/images/logo/gemini.jpg" alt="Gemini" />
                }
                @case ('ai_overview') {
                  <svg width="20" height="20" viewBox="0 0 24 24" aria-label="AI Overview">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                }
                @case ('ai_mode') {
                  <svg width="20" height="20" viewBox="0 0 24 24" aria-label="AI Mode">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                }
                @default {
                  <span class="card-platform-fallback">{{ getPlatformLabel(item.platform) }}</span>
                }
              }
            </span>
            <h4 class="card-prompt">{{ item.query }}</h4>
          </div>
          <p class="card-snippet">{{ truncateAnswer(item.answer_text) }}</p>
          @if ((item.cited_domains && item.cited_domains.length) || (item.mentioned_brands && item.mentioned_brands.length)) {
            <div class="card-mentions mention-chips">
              @if (item.cited_domains?.length) {
                @for (dom of item.cited_domains; track dom) {
                  <img [src]="getFaviconUrl(dom)" alt="" class="mention-domain-favicon" [title]="dom" (error)="onFaviconError($event)" />
                }
              }
              @for (brandName of item.mentioned_brands || []; track brandName) {
                <span class="mention-chip" [title]="brandName">
                  <span class="mention-chip-initial">{{ getBrandInitial(brandName) }}</span>
                  <span class="mention-chip-name">{{ brandName }}</span>
                </span>
              }
            </div>
          }
        </button>
      }
    </div>
    @if (hasMoreTopPrompts) {
      <div class="expand-wrap">
        <button type="button" class="expand-button" (click)="topPromptsExpanded = true">
          Expand to show all {{ getTopPromptsByPlatform().length }} prompts
        </button>
      </div>
    }
  </section>
  } @else if (llmComparisonData && llmComparisonData.length) {
  <section class="top-prompts-by-platform-section empty">
    <p class="empty-message">Run AI Visibility to see AI Top Prompts.</p>
  </section>
  }

  <!-- Top Domains table (same as AI Citation tab) -->
  @if (hasSimpleTopDomainsData) {
  <section class="top-domains-section">
    <div class="top-domains-section-header">
      <h3 class="section-title">Top Domains</h3>
      <button mat-stroked-button type="button" color="primary" (click)="viewAllTopDomainsInAiCitations()" class="view-all-domains-btn">
        View All
      </button>
    </div>
    <p class="section-subtitle">Click a row to see cited pages for that domain.</p>
    @if (!selectedDomainForPages) {
    <div class="top-domains-table">
      <app-common-simple-table
        [data]="simpleSourceTableData"
        [columns]="simpleSourceTableColumns"
        [config]="simpleSourceTableConfig"
        title=""
        subtitle=""
        (rowClick)="onSimpleSourceRowClick($event)">
      </app-common-simple-table>
    </div>
    }
    @if (selectedDomainForPages) {
    <div class="sites-for-domain">
      <div class="cited-pages-filter-bar">
        <div class="sites-domain-left">
          <button mat-button (click)="clearSelectedDomainForPages()" class="back-to-all-btn">
            <mat-icon>arrow_back</mat-icon> Back
          </button>
          <h4>Sites for {{ selectedDomainForPages }}</h4>
        </div>
      </div>
      @if (citedPagesForSelectedDomain.length > 0) {
      <app-common-simple-table
        [data]="citedPagesForSelectedDomain"
        [columns]="simpleCitedPagesTableColumns"
        [config]="simpleCitedPagesTableConfig"
        title=""
        subtitle="">
      </app-common-simple-table>
      } @else {
      <p class="no-pages-message">No cited pages for this domain.</p>
      }
    </div>
    }
  </section>
  }

  <!-- AI Visibility Recommendations: Short-Term / Mid-Term / Long-Term -->
  @if (aiVisibilityRecommendations.short_term.length > 0 || aiVisibilityRecommendations.mid_term.length > 0 || aiVisibilityRecommendations.long_term.length > 0) {
  <section class="ai-visibility-recommendations-section">
    <h3 class="section-title">AI Visibility Recommendations</h3>
    <p class="section-subtitle">Actionable steps to improve AI visibility and citation coverage.</p>
    <div class="recommendations-cards">
      <div class="recommendation-card short-term">
        <h4 class="card-term-title">Short-Term (0–3 months)</h4>
        <ul>
          @for (item of aiVisibilityRecommendations.short_term; track item.title) {
            <li>
              <strong>{{ item.title }}</strong>
              <span>{{ item.description }}</span>
            </li>
          }
        </ul>
      </div>
      <div class="recommendation-card mid-term">
        <h4 class="card-term-title">Mid-Term (3–12 months)</h4>
        <ul>
          @for (item of aiVisibilityRecommendations.mid_term; track item.title) {
            <li>
              <strong>{{ item.title }}</strong>
              <span>{{ item.description }}</span>
            </li>
          }
        </ul>
      </div>
      <div class="recommendation-card long-term">
        <h4 class="card-term-title">Long-Term (12+ months)</h4>
        <ul>
          @for (item of aiVisibilityRecommendations.long_term; track item.title) {
            <li>
              <strong>{{ item.title }}</strong>
              <span>{{ item.description }}</span>
            </li>
          }
        </ul>
      </div>
    </div>
  </section>
  }

  <!-- Analysis Details (moved to last, before notes) -->
  @if (overviewData.metadata) {
  <div class="metadata-section">
    <h3>Analysis Details</h3>
    <div class="metadata-content">
      <div class="metadata-grid">
        <div class="metadata-item">
          <strong>Primary Industry:</strong> {{ overviewData.metadata.industry || 'N/A' }}
        </div>
        <div class="metadata-item">
          <strong>Primary Category:</strong> {{ overviewData.metadata.category || 'N/A' }}
        </div>
        <!-- <div class="metadata-item">
          <strong>Your Brand:</strong> {{ overviewData.metadata.your_brand || 'N/A' }}
        </div>
        <div class="metadata-item">
          <strong>Competitors:</strong> {{ overviewData.metadata.competitors && overviewData.metadata.competitors.length > 0 ? overviewData.metadata.competitors.join(', ') : 'None' }}
        </div>
        <div class="metadata-item">
          <strong>Brands Analyzed:</strong> {{ overviewData.metadata.brands_analyzed || 0 }}
        </div>
        <div class="metadata-item">
          <strong>Data Source:</strong> {{ overviewData.metadata.data_source || 'N/A' }}
        </div>
        <div class="metadata-item">
          <strong>Response Time:</strong> {{ overviewData.metadata.response_time ? (overviewData.metadata.response_time | number:'1.2-2') + 's' : 'N/A' }}
        </div> -->
      </div>
    </div>
  </div>
  }
  </div>
</div>
}